import React, { useState } from 'react';
import { View, Modal, Text, Pressable, StyleSheet } from 'react-native';
import { Calendar } from 'react-native-calendars';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onConfirm: (range: DateRange) => void;
  initialRange?: DateRange;
}

const DateRangePicker: React.FC<Props> = ({
  visible,
  onClose,
  onConfirm,
  initialRange,
}) => {
  const [range, setRange] = useState<DateRange>(
    initialRange || { startDate: null, endDate: null }
  );

  const onDayPress = (day: any) => {
    if (!range.startDate || (range.startDate && range.endDate)) {
      setRange({ startDate: day.dateString, endDate: null });
    } else if (range.startDate && !range.endDate) {
      if (day.dateString < range.startDate) {
        setRange({ startDate: day.dateString, endDate: range.startDate });
      } else {
        setRange({ ...range, endDate: day.dateString });
      }
    }
  };

  // Mark selected range
  const getMarkedDates = () => {
    const marked: any = {};
    if (range.startDate) {
      marked[range.startDate] = {
        startingDay: true,
        color: '#39BEB7',
        textColor: 'white',
      };
      if (range.endDate) {
        let start = new Date(range.startDate);
        let end = new Date(range.endDate);
        let curr = new Date(start);
        while (curr <= end) {
          const ds = curr.toISOString().split('T')[0];
          if (ds !== range.startDate && ds !== range.endDate) {
            marked[ds] = { color: '#B2F1EC', textColor: 'black' };
          }
          curr.setDate(curr.getDate() + 1);
        }
        marked[range.endDate] = {
          endingDay: true,
          color: '#39BEB7',
          textColor: 'white',
        };
      }
    }
    return marked;
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <Text style={styles.title}>Select Date Range</Text>
          <View style={styles.calendarWrapper}>
            <Calendar
              style={styles.calendar}
              markingType="period"
              markedDates={getMarkedDates()}
              onDayPress={onDayPress}
              theme={{
                backgroundColor: '#23262B',
                calendarBackground: '#23262B',
                selectedDayBackgroundColor: '#39BEB7',
                todayTextColor: '#39BEB7',
                arrowColor: '#39BEB7',
                dayTextColor: '#E8E8E9',
                monthTextColor: '#E8E8E9',
                textSectionTitleColor: '#8D8E90',
                textDisabledColor: '#636363',
                textDayFontWeight: '500',
                textMonthFontWeight: 'bold',
                textDayHeaderFontWeight: 'bold',
                textDayFontSize: 16,
                textMonthFontSize: 18,
                textDayHeaderFontSize: 14,
              }}
            />
          </View>
          <View style={styles.buttonRow}>
            <Pressable style={styles.button} onPress={onClose}>
              <Text style={styles.buttonText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[styles.button, { backgroundColor: '#39BEB7' }]}
              onPress={() => {
                if (range.startDate && range.endDate) {
                  onConfirm(range);
                  onClose();
                }
              }}
              disabled={!(range.startDate && range.endDate)}
            >
              <Text style={[styles.buttonText, { color: 'white' }]}>
                Confirm
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: '#23262B',
    borderRadius: 16,
    padding: 16,
    width: 350,
    maxWidth: '95%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
    alignSelf: 'center',
  },
  calendarWrapper: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#23262B',
    borderRadius: 12,
    padding: 4,
    marginBottom: 8,
  },
  calendar: {
    backgroundColor: '#23262B',
    borderRadius: 12,
    width: 320,
    alignSelf: 'center',
    elevation: 0,
  },
  buttonRow: {
    flexDirection: 'row',
    marginTop: 16,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  button: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 8,
    backgroundColor: '#292D33',
    marginHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: '#39BEB7',
    fontWeight: 'bold',
    fontSize: 16,
    textAlign: 'center',
  },
});

export default DateRangePicker;
